import React, { useMemo, type FC, type ReactNode, type Key } from 'react'
import { SearchOutlined } from '@ant-design/icons'
import {
  Table, Input, Space,
  Flex, ConfigProvider,
  type TableColumnType,
  type TableProps
} from 'antd'
import dayjs from 'dayjs'
import { useTableSorting, type IMSTableColumnType } from '@choreograph/layout'
import { useFilteredData } from '../../../constants/hooks/useFilteredData'
import { type IUserPermission } from '../../../constants/types/common'
import { getTablePaginationProp, setPermissionRowKey } from '../../../constants/helpers/common'
import { EmptyTablePlaceholder } from '../../shared'

const formatExpiresAt = (expiresAt: string | null | undefined): string => {
  if (!expiresAt) return '—'
  return dayjs(expiresAt).format('YYYY-MM-DD')
}

const dataColumns: Array<IMSTableColumnType<IUserPermission>> = [
  {
    title: 'Namespace',
    dataIndex: 'namespace',
    key: 'namespace',
    sorterProps: {
      dataType: 'text'
    }
  },
  {
    title: 'Resource Type',
    dataIndex: 'resourceType',
    key: 'resourceType',
    sorterProps: {
      dataType: 'text'
    }
  },
  {
    title: 'Resource ID',
    dataIndex: 'resourceId',
    key: 'resourceId',
    sorterProps: {
      dataType: 'numerical'
    }
  },
  {
    title: 'Relation',
    dataIndex: 'relation',
    key: 'relation',
    sorterProps: {
      dataType: 'text'
    }
  },
  {
    title: 'Expires At',
    dataIndex: 'expiresAt',
    key: 'expiresAt',
    sorterProps: {
      dataType: 'text'
    },
    render: (value: string | null | undefined) => formatExpiresAt(value)
  }
]

type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection']

interface IPermissionTableProps {
  permissions: IUserPermission[]
  loading: boolean
  selectedRowKeys: Key[]
  setSelectedRowKeys: (keys: Key[]) => void
  rowActionButtons?: TableColumnType<IUserPermission>
  bulkActionRow?: ReactNode
}

export const PermissionTable: FC<IPermissionTableProps> = ({
  permissions,
  loading,
  selectedRowKeys,
  setSelectedRowKeys,
  rowActionButtons,
  bulkActionRow
}) => {
  const { handleSortable } = useTableSorting(true)
  const { filteredTableData, handleFilter } = useFilteredData(permissions, ['namespace', 'resourceType', 'resourceId', 'relation'])

  const pagination = useMemo(() => (
    getTablePaginationProp<IUserPermission>('permissions', filteredTableData.length)
  ), [filteredTableData.length])

  const columns: Array<TableColumnType<IUserPermission>> = useMemo(() => (
    [
      ...dataColumns,
      ...(rowActionButtons) ? [rowActionButtons] : []
    ].map(c => handleSortable(c))
  ), [handleSortable, rowActionButtons])

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection: TableRowSelection<IUserPermission> = {
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true
  }

  return (
    <Flex vertical={true}>
      <Flex justify='space-between' align='center'>
        <Space.Compact className='input-search'>
          <Space.Addon><SearchOutlined /></Space.Addon>
          <Input
            placeholder='Search permissions'
            onChange={handleFilter}
            allowClear={true}
          />
        </Space.Compact>
        {bulkActionRow ? bulkActionRow : null}
      </Flex>

      <ConfigProvider
        renderEmpty={() => (
          <EmptyTablePlaceholder
            loading={loading}
            title='No Permissions'
            message=' There are currently no permissions to view' />
        )}>
        <Table
          className='permissions-table'
          rowSelection={rowSelection}
          loading={loading}
          columns={columns}
          dataSource={filteredTableData}
          rowKey={record => setPermissionRowKey(record)}
          {...pagination}
        />
      </ConfigProvider>
    </Flex>
  )
}
